
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import tinycolor from 'tinycolor2';
import { Provider } from '../models/provider.model';

export interface ProviderBranding {
  isResolving: boolean;
  isWhiteLabel: boolean;
  headerImage?: string;
  providerTitle?: string;
}

const DEFAULT_BRANDING: ProviderBranding = {
  isResolving: false,
  isWhiteLabel: false,
};

const THEME_PROPERTY_GROUPS = {
  whiteLabel: [
    '--white-label-black',
    '--white-label-white',
  ],
  background: [
    '--lad-confetti-background-image',
  ],
  header: [
    '--custom-header-color',
    '--contrast-custom-header-color',
  ],
  dialog: [
    '--custom-dialog-surface-color',
    '--custom-dialog-title-color',
    '--custom-dialog-content-color',
    '--custom-dialog-button-container-color',
    '--custom-dialog-button-label-color',
  ],
  button: [
    '--custom-button-color',
    '--contrast-custom-button-color',
    '--primary-base-color',
    '--primary-base-color-20',
    '--primary-base-color-30',
    '--primary-base-color-50',
    '--primary-lighter-color',
    '--primary-darker-color',
    '--accent-base-color',
    '--accent-base-color-50',
    '--accent-lighter-color',
    '--accent-darker-color',
    '--purple-base-color',
    '--red-base-color',
  ],
} as const;

type ThemePropertyGroup = keyof typeof THEME_PROPERTY_GROUPS;
type ThemeTokenMap = Record<string, string>;

@Injectable({
  providedIn: 'root'
})
export class ProviderCustomizeService {
  private readonly brandingSubject = new BehaviorSubject<ProviderBranding>(DEFAULT_BRANDING);
  private readonly themePropertyGroups = THEME_PROPERTY_GROUPS;

  constructor(
    @Inject(DOCUMENT) private document: Document,
  ) {}

  get branding$(): Observable<ProviderBranding> {
    return this.brandingSubject.asObservable();
  }

  beginProviderResolution(): void {
    this.removeThemeGroups('whiteLabel', 'header', 'dialog', 'button');
    this.applyThemeTokens(this.getHideLadBackgroundTokens());
    this.brandingSubject.next({ isResolving: true, isWhiteLabel: false });
  }

  resolveProvider(provider?: Provider | null): void {
    if (provider?.whiteLabel !== true) {
      this.clear();
      return;
    }

    this.applyThemeTokens(this.getWhiteLabelTokens());
    this.applyThemeTokens(this.getHideLadBackgroundTokens());

    if (provider.customHeaderColor) {
      this.applyThemeTokens(this.getHeaderThemeTokens(provider.customHeaderColor));
    } else {
      this.removeThemeGroups('header');
    }

    if (provider.customButtonColor) {
      this.applyThemeTokens(this.getButtonThemeTokens(provider.customButtonColor));
      this.applyThemeTokens(this.getDialogThemeTokens(provider.customButtonColor));
    } else {
      this.removeThemeGroups('dialog', 'button');
    }

    this.brandingSubject.next({
      isResolving: false,
      isWhiteLabel: true,
      headerImage: provider.header,
      providerTitle: provider.title || provider.name,
    });
  }

  clear(): void {
    this.removeThemeGroups('whiteLabel', 'background', 'header', 'dialog', 'button');
    this.brandingSubject.next(DEFAULT_BRANDING);
  }

  private getWhiteLabelTokens(): ThemeTokenMap {
    return {
      '--white-label-black': '#000000',
      '--white-label-white': '#ffffff',
    };
  }

  private getHideLadBackgroundTokens(): ThemeTokenMap {
    return {
      '--lad-confetti-background-image': 'none',
    };
  }

  private getHeaderThemeTokens(headerColor: string): ThemeTokenMap {
    const hex = this.normalizeHex(headerColor);
    if (!hex) return {};

    return {
      '--custom-header-color': hex,
      '--contrast-custom-header-color': this.getContrastColor(hex),
    };
  }

  private getButtonThemeTokens(buttonColor: string): ThemeTokenMap {
    const hex = this.normalizeHex(buttonColor);
    if (!hex) return {};

    const lighterColor = tinycolor(hex).lighten(5).toHexString().toUpperCase();
    const darkerColor = tinycolor(hex).darken(5).toHexString().toUpperCase();

    return {
      '--custom-button-color': hex,
      '--contrast-custom-button-color': this.getContrastColor(hex),
      '--primary-base-color': hex,
      '--primary-base-color-20': `${hex}33`,
      '--primary-base-color-30': `${hex}4D`,
      '--primary-base-color-50': `${hex}80`,
      '--primary-lighter-color': lighterColor,
      '--primary-darker-color': darkerColor,
      '--accent-base-color': hex,
      '--accent-base-color-50': `${hex}80`,
      '--accent-lighter-color': lighterColor,
      '--accent-darker-color': darkerColor,
      '--purple-base-color': hex,
      '--red-base-color': hex,
    };
  }

  private getDialogThemeTokens(buttonColor: string): ThemeTokenMap {
    const hex = this.normalizeHex(buttonColor);
    if (!hex) return {};

    const contrastColor = this.getContrastColor(hex);

    return {
      '--custom-dialog-surface-color': hex,
      '--custom-dialog-title-color': contrastColor,
      '--custom-dialog-content-color': contrastColor,
      '--custom-dialog-button-container-color': contrastColor,
      '--custom-dialog-button-label-color': hex,
    };
  }

  private applyThemeTokens(tokens: ThemeTokenMap): void {
    Object.entries(tokens).forEach(([property, value]) => this.setThemeProperty(property, value));
  }

  private removeThemeGroups(...groups: ThemePropertyGroup[]): void {
    const properties = groups.reduce<string[]>((styles, group) => {
      styles.push(...this.themePropertyGroups[group]);
      return styles;
    }, []);

    this.removeStyles(properties);
  }

  private removeStyles(properties: readonly string[]): void {
    properties.forEach((property) => this.document.documentElement.style.removeProperty(property));
  }

  private normalizeHex(color?: string): string | null {
    const hex = color?.trim();
    return hex && /^#[0-9a-fA-F]{6}$/.test(hex) ? hex.toUpperCase() : null;
  }

  private getContrastColor(hex: string): string {
    return tinycolor(hex).isLight() ? '#000000' : '#ffffff';
  }

  private setThemeProperty(property: string, value: string): void {
    this.document.documentElement.style.setProperty(property, value, 'important');
  }
}
